/** Project-owned, illustrative materials. No catalogue photographs are used as textures.
 * Source/type evidence and outstanding sample calibration: docs/configurator-glass-catalogue.md.
 * Optical numbers are render parameters, NOT measured transmission/privacy specifications.
 */
export type ProformGlassPattern = {
  type:
    | 'clear-squares'
    | 'dots'
    | 'lenses'
    | 'stripes'
    | 'flutes'
    | 'waves'
    | 'hammered'
    | 'bark'
    | 'delta'
    | 'ice'
    | 'grain'
    | 'crosshatch'
    | 'tiles'
    | 'woven'
    | 'checkerboard';
  pitchMm: number;
  featureSizeMm: number;
  clearRoughness: number;
  frostedRoughness: number;
  reliefMm: number;
  directional?: boolean;
  seed?: number;
};

export type ProformGlassMaterialDescriptor = {
  id: string;
  label: string;
  calibration: 'provisional' | 'generic';
  family: 'clear' | 'frosted' | 'ornament' | 'lacquer' | 'mirror' | 'unknown';
  color: string;
  ior: number;
  roughness: number;
  thicknessMm: number;
  transmission: number;
  ornamentSide?: 'inner' | 'outer';
  horizontal?: boolean;
  pattern?: ProformGlassPattern;
  warning?: 'reference-needed' | 'custom-glass' | 'color-needed';
};

export type ProformGlassPreset = ProformGlassMaterialDescriptor & {
  groupCode: string;
  fillingCode: string;
  /** Evidence verifies product family/appearance, never the numeric shader parameters. */
  source:
    | 'float'
    | 'laminated'
    | 'satin'
    | 'lacomat'
    | 'sevasa'
    | 'masterglass'
    | 'imagin'
    | 'decorglass'
    | 'planilaque'
    | 'mirror'
    | 'supplier-name';
  colorSpecification?: '10' | '20';
};

export const GENERIC_PROFORM_GLASS: ProformGlassMaterialDescriptor = {
  id: 'generic-clear',
  label: 'Orientační sklo',
  calibration: 'generic',
  family: 'unknown',
  color: '#f5faf8',
  ior: 1.5,
  roughness: 0.015,
  thicknessMm: 4,
  transmission: 0.96,
};

function preset(
  groupCode: string,
  fillingCode: string,
  label: string,
  family: ProformGlassPreset['family'],
  source: ProformGlassPreset['source'],
  options: Partial<ProformGlassPreset> = {},
): ProformGlassPreset {
  return {
    ...GENERIC_PROFORM_GLASS,
    id: `${groupCode.toLowerCase()}-${fillingCode.toLowerCase()}`,
    groupCode,
    fillingCode,
    label,
    family,
    source,
    calibration: 'provisional',
    ...options,
  };
}

function pattern(
  type: ProformGlassPattern['type'],
  pitchMm: number,
  featureSizeMm: number,
  frostedRoughness = 0.28,
  reliefMm = 0.12,
  seed = 1,
): ProformGlassPattern {
  return {
    type,
    pitchMm,
    featureSizeMm,
    frostedRoughness,
    clearRoughness: 0.025,
    reliefMm,
    seed,
    directional: ['stripes', 'flutes', 'waves', 'bark', 'woven'].includes(type),
  };
}

const bronze = '#bd9b7b';
const frost = (color = '#f5f8f5') => ({ color, roughness: 0.42, transmission: 0.92 });
function ornament(
  code: string,
  label: string,
  source: ProformGlassPreset['source'],
  relief: ProformGlassPattern,
  options: Partial<ProformGlassPreset> = {},
) {
  return preset('ORNAM', code, label, 'ornament', source, {
    roughness: 1,
    pattern: relief,
    ...options,
  });
}
function lacquer(code: string, label: string, color: string, colorSpecification?: '10' | '20') {
  return preset('LAK', code, label, 'lacquer', 'planilaque', {
    color,
    transmission: 0,
    roughness: 0.07,
    colorSpecification,
  });
}

/** Exact (group, filling) pairs from the supplier's 59-entry catalogue. Grilles are excluded. */
export const PROFORM_GLASS_PRESETS: readonly ProformGlassPreset[] = [
  preset('CIRA', 'SKCI', 'Čiré sklo 4 mm', 'clear', 'float'),
  preset('CIRA', 'DIAM', 'Diamant · extra čiré sklo 4 mm', 'clear', 'float', {
    color: '#ffffff',
    transmission: 0.99,
    roughness: 0.01,
  }),
  preset('CIRA', 'ST221C', 'Stadip / Connex · čiré vrstvené sklo', 'clear', 'laminated', {
    transmission: 0.94,
    thicknessMm: 4.38,
  }),
  preset('CIRA', 'SKCIBR', 'Čiré sklo · bronz', 'clear', 'float', {
    color: bronze,
    transmission: 0.94,
  }),

  preset('MAT', 'LACO', 'Lacomat White', 'frosted', 'lacomat', {
    ...frost('#ffffff'),
    roughness: 0.55,
    transmission: 0.86,
  }),
  preset('MAT', 'SATI', 'Satinato', 'frosted', 'satin', frost()),
  preset('MAT', 'SATIBR', 'Satinato · bronz', 'frosted', 'satin', frost(bronze)),
  preset('MAT', 'SATIMO', 'Satinato · modré', 'frosted', 'satin', frost('#9bbccc')),
  preset('MAT', 'SATISE', 'Satinato · šedé', 'frosted', 'satin', frost('#a5aaac')),
  preset('MAT', 'SATIZE', 'Satinato · zelené', 'frosted', 'satin', frost('#a5c3ad')),
  // SEVASA catalogue p35: coarse/fine alternating clear and satin checkerboards.
  // Repeat dimensions are provisional until supplied/measured, not inferred from the photo.
  preset('MAT', 'SA260', 'SatenDecor 260', 'frosted', 'sevasa', {
    roughness: 1,
    pattern: pattern('checkerboard', 40, 20, 0.42, 0.025),
  }),
  preset('MAT', 'SA274', 'SatenDecor 274', 'frosted', 'sevasa', {
    roughness: 1,
    pattern: pattern('checkerboard', 12, 6, 0.42, 0.025),
  }),
  preset('MAT', 'SAMR11', 'SatenDecor · Mil Rayas 1 × 1', 'frosted', 'sevasa', {
    roughness: 1,
    pattern: pattern('stripes', 2, 1, 0.42, 0.015),
  }),
  preset('MAT', 'SAMR22', 'SatenDecor · Mil Rayas 2 × 2', 'frosted', 'sevasa', {
    roughness: 1,
    pattern: pattern('stripes', 4, 2, 0.42, 0.015),
  }),
  preset('MAT', 'SAMR00', 'SatenDecor · Mil Rayas 10 × 10', 'frosted', 'sevasa', {
    roughness: 1,
    pattern: pattern('stripes', 20, 10, 0.42, 0.015),
  }),
  preset('MAT', 'ST221M', 'Stadip / Connex · matné vrstvené sklo', 'frosted', 'laminated', {
    ...frost('#ffffff'),
    transmission: 0.85,
    roughness: 0.52,
    thicknessMm: 4.38,
  }),

  ornament('ADBR', 'Altdeutsch · bronz', 'decorglass', pattern('waves', 90, 20, 0.11, 0.25, 3), {
    color: bronze,
  }),
  ornament('ADCI', 'Altdeutsch · čirý', 'decorglass', pattern('waves', 90, 20, 0.11, 0.25, 3)),
  ornament('CALI', 'California', 'supplier-name', pattern('hammered', 65, 9, 0.27, 0.22, 5), {
    warning: 'reference-needed',
  }),
  ornament('COLO', 'Colorado', 'supplier-name', pattern('grain', 36, 3, 0.34, 0.18, 9), {
    warning: 'reference-needed',
  }),
  ornament('DECI', 'Delta · čiré', 'imagin', pattern('delta', 95, 5, 0.19, 0.28, 7)),
  ornament('DEMA', 'Delta · mat', 'imagin', {
    ...pattern('delta', 95, 5, 0.48, 0.28, 7),
    clearRoughness: 0.31,
  }),
  ornament(
    'CHBR',
    'Chinchila / Vision · bronz',
    'imagin',
    pattern('grain', 24, 1.5, 0.31, 0.16, 4),
    { color: bronze },
  ),
  ornament('CHCI', 'Chinchila / Vision · čirá', 'imagin', pattern('grain', 24, 1.5, 0.31, 0.16, 4)),
  ornament('ICE', 'Ice', 'supplier-name', pattern('ice', 60, 7, 0.3, 0.24, 11), {
    warning: 'reference-needed',
  }),
  ornament('INDI', 'Indiana', 'supplier-name', pattern('hammered', 42, 5, 0.32, 0.25, 13), {
    warning: 'reference-needed',
  }),
  ornament('KAMI', 'Kathedral min.', 'imagin', pattern('hammered', 32, 6, 0.18, 0.24, 2)),
  ornament('KRIZ', 'Krizet / Listral D', 'decorglass', pattern('crosshatch', 6, 0.8, 0.25, 0.1)),
  ornament('KUBR', 'Kůra · bronz', 'imagin', pattern('bark', 55, 5, 0.21, 0.28, 6), {
    color: bronze,
  }),
  ornament('KUCI', 'Kůra · čirá', 'imagin', pattern('bark', 55, 5, 0.21, 0.28, 6)),
  ornament('MARI', 'Maris', 'decorglass', pattern('waves', 45, 8, 0.24, 0.16, 2), {
    warning: 'reference-needed',
  }),
  ornament(
    'MACA',
    'Master-Carré',
    'masterglass',
    { ...pattern('clear-squares', 10, 2, 0.27, 0.12), clearRoughness: 0.015 },
    { id: 'master-carre' },
  ),
  ornament('MALE', 'Master-Lens', 'masterglass', pattern('lenses', 15, 5, 0.14, 0.22)),
  ornament('MALI', 'Master-Ligne', 'masterglass', pattern('stripes', 10, 2, 0.3, 0.12)),
  ornament('MAPO', 'Master-Point', 'masterglass', pattern('dots', 5, 1.2, 0.4, 0.1)),
  ornament('MARA', 'Master-Ray', 'masterglass', pattern('flutes', 8, 5, 0.32, 0.18), {
    warning: 'reference-needed',
  }),
  ornament('MASH', 'Master-Shine', 'masterglass', pattern('lenses', 8, 3, 0.12, 0.16)),
  ornament('NEMO', 'Nemo', 'decorglass', pattern('hammered', 50, 8, 0.3, 0.19, 15), {
    warning: 'reference-needed',
  }),
  ornament('QUAD', 'Quadra', 'supplier-name', pattern('tiles', 12, 10, 0.22, 0.18), {
    warning: 'reference-needed',
  }),
  ornament('QUTR', 'Quatrix · čirý', 'supplier-name', pattern('tiles', 8, 6, 0.28, 0.2), {
    warning: 'reference-needed',
  }),
  ornament('SPOT', 'Spotlyte', 'decorglass', pattern('lenses', 8, 5, 0.17, 0.22), {
    warning: 'reference-needed',
  }),
  ornament('TEXA', 'Texas', 'supplier-name', pattern('ice', 45, 5, 0.33, 0.22, 21), {
    warning: 'reference-needed',
  }),
  ornament('THEL', 'Thela', 'decorglass', pattern('woven', 7, 1, 0.24, 0.12)),
  ornament('PUNTO', 'Punto', 'supplier-name', pattern('dots', 4, 1.4, 0.23, 0.12), {
    warning: 'reference-needed',
  }),
  ornament('FLCI', 'Flutes · čirý', 'imagin', pattern('flutes', 12, 10, 0.11, 0.28)),

  lacquer('SKCINCS', 'Lakované čiré sklo · NCS', '#e4e4df', '20'),
  lacquer('SKCIRAL', 'Lakované čiré sklo · RAL', '#e4e4df', '10'),
  lacquer('DIAMNCS', 'Lakovaný Diamant · NCS', '#e4e4df', '20'),
  lacquer('DIAMRAL', 'Lakovaný Diamant · RAL', '#e4e4df', '10'),
  lacquer('PL9003', 'Planilaque · bílá RAL 9003', '#f4f4f0'),
  lacquer('PL1015', 'Planilaque · slonovina RAL 1015', '#e6d2b5'),
  lacquer('PL6027', 'Planilaque · mátová RAL 6027', '#7ebabb'),
  lacquer('PLZLUT', 'Planilaque · žlutá', '#e9be30'),
  lacquer('PLSEDA', 'Planilaque · titanová šedá', '#878b8c'),
  // Supplier literally labels this RAL1586; do not silently substitute a different RAL code.
  {
    ...lacquer('PL1586', 'Planilaque · červená (kód dodavatele PL1586)', '#b52a35'),
    warning: 'reference-needed',
  },
  lacquer('PL3004', 'Planilaque · tmavá červená RAL 3004', '#651e2a'),
  lacquer('PL9004', 'Planilaque · černá RAL 9004', '#282828'),
  preset('JINA', 'ZRCA04', 'Zrcadlo 4 mm', 'mirror', 'mirror', {
    color: '#f7f7f7',
    roughness: 0.035,
    transmission: 0,
  }),
  preset('JINA', 'JINE', 'Jiné sklo', 'unknown', 'supplier-name', {
    calibration: 'generic',
    warning: 'custom-glass',
  }),
];

// Prevent accidental mutation by consumers; the resolver returns fresh descriptors.
PROFORM_GLASS_PRESETS.forEach(item => {
  if (item.pattern) Object.freeze(item.pattern);
  Object.freeze(item);
});
Object.freeze(PROFORM_GLASS_PRESETS);

export function findProformGlassPreset(groupCode?: string, fillingCode?: string) {
  const group = groupCode?.trim().toUpperCase();
  const code = fillingCode?.trim().toUpperCase();
  return PROFORM_GLASS_PRESETS.find(item => item.groupCode === group && item.fillingCode === code);
}
